import { useState } from 'react'
import { CheckCircle2, RotateCcw, History, ChevronDown, ChevronUp } from 'lucide-react'
import { formatCurrency, formatDate } from '@utils/format'

export interface QuoteReviewStatusProps {
  status: string // deal.status
  submittedByName?: string | null
  submittedAt?: string | null
  implRequestedAt?: string | null
  implCompletedAt?: string | null
  implCompletedByName?: string | null
  implFeeBefore?: number | null
  implFeeAfter?: number | null
  reviewedBy?: string | null // email
  reviewedAt?: string | null
  reviewNotes?: string | null
  finalizedBy?: string | null // email
  finalizedAt?: string | null
}

// Derive a display name from an email local-part, e.g. jacqui.tan@x → "Jacqui Tan".
function nameFromEmail(email: string | null | undefined): string | null {
  if (!email) return null
  return email.split('@')[0].split('.').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
}

interface TimelineEvent {
  at: string
  label: string
  who?: string | null
  detail?: string | null
  tone: 'muted' | 'amber' | 'emerald'
}

/**
 * Persistent review marker on the quote: a green "Approved" tag (or amber "Sent back")
 * once the deal has been through review, plus an expandable review-history timeline built
 * from the deal's review-lifecycle fields. Renders nothing for a quote with no review
 * history (e.g. a fresh draft). The active in_review / impl-review states are handled by
 * ReviewBanner — this covers the post-review (finalised / sent-back) states.
 */
export function QuoteReviewStatus(props: QuoteReviewStatusProps) {
  const [open, setOpen] = useState(false)

  const isApproved = props.status === 'finalized'
  // Sent back = a reviewer acted but it's not finalised (returned to draft).
  const isSentBack = !isApproved && !!props.reviewedAt

  // Build the lifecycle timeline from whatever milestones are present.
  const events: TimelineEvent[] = []
  if (props.submittedAt) {
    events.push({ at: props.submittedAt, label: 'Submitted for review', who: props.submittedByName, tone: 'muted' })
  }
  if (props.implRequestedAt) {
    events.push({ at: props.implRequestedAt, label: 'Diverted to Implementations', tone: 'muted' })
  }
  if (props.implCompletedAt) {
    const before = props.implFeeBefore ?? 0
    const after = props.implFeeAfter ?? 0
    events.push({
      at: props.implCompletedAt,
      label: 'Implementation fee set',
      who: props.implCompletedByName,
      detail: `${formatCurrency(before)} → ${formatCurrency(after)}`,
      tone: 'muted',
    })
  }
  if (isSentBack && props.reviewedAt) {
    events.push({ at: props.reviewedAt, label: 'Sent back for revision', who: nameFromEmail(props.reviewedBy), detail: props.reviewNotes, tone: 'amber' })
  }
  if (isApproved && props.finalizedAt) {
    events.push({
      at: props.finalizedAt,
      label: 'Approved & finalised',
      who: nameFromEmail(props.finalizedBy) || nameFromEmail(props.reviewedBy),
      detail: props.reviewNotes,
      tone: 'emerald',
    })
  }
  events.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())

  // Nothing to show for a quote that's never been through review.
  if (!isApproved && !isSentBack && events.length === 0) return null

  const tagReviewer = nameFromEmail(props.finalizedBy) || nameFromEmail(props.reviewedBy)
  const tagDate = props.finalizedAt || props.reviewedAt

  return (
    <div className={`rounded-lg border ${isApproved ? 'border-emerald-500/30 bg-emerald-500/5' : isSentBack ? 'border-amber-500/30 bg-amber-500/5' : 'border-[var(--border-color)] bg-[var(--bg-secondary)]'}`}>
      <div className="flex items-center gap-2 px-4 py-2.5">
        {isApproved ? (
          <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />
        ) : isSentBack ? (
          <RotateCcw size={16} className="text-amber-400 flex-shrink-0" />
        ) : (
          <History size={16} className="text-[var(--text-muted)] flex-shrink-0" />
        )}
        <span className="text-sm font-medium text-[var(--text-primary)]">
          {isApproved ? 'Approved' : isSentBack ? 'Sent back for revision' : 'Review history'}
        </span>
        {(isApproved || isSentBack) && tagReviewer && (
          <span className="text-xs text-[var(--text-muted)]">
            by {tagReviewer}{tagDate ? ` · ${formatDate(tagDate)}` : ''}
          </span>
        )}
        {events.length > 0 && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="ml-auto flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <History size={12} />
            Review history
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
      </div>

      {open && events.length > 0 && (
        <div className="border-t border-[var(--border-color)] px-4 py-3">
          <ol className="space-y-2.5">
            {events.map((e, i) => (
              <li key={i} className="flex gap-3 text-xs">
                <span
                  className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    e.tone === 'emerald' ? 'bg-emerald-400' : e.tone === 'amber' ? 'bg-amber-400' : 'bg-[var(--text-muted)]'
                  }`}
                />
                <div className="min-w-0">
                  <div className="text-[var(--text-primary)]">
                    {e.label}
                    {e.detail && <span className="text-[var(--text-muted)]"> — {e.detail}</span>}
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)]">
                    {e.who ? `${e.who} · ` : ''}{formatDate(e.at)}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}
